'use client'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Box, CircularProgress, Link, Typography } from '@mui/material'
import dynamic from 'next/dynamic'
// See hotelMapView for the detail: kept out of the `ssr: false` canvas so Leaflet's
// positioning rules ship in the page stylesheet, which carries the full assetPrefix, rather
// than in a lazy chunk whose CSS link loses the CDN origin (vercel/next.js#72470).
import 'leaflet/dist/leaflet.css'

const DetailsMapCanvas = dynamic(() => import('./detailsMapCanvas'), {
    ssr: false,
    loading: () => (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'neutral.100' }}>
            <CircularProgress size={24} />
        </Box>
    )
})

export interface MapSectionProps {
    lat: number
    lng: number
    mapsUrl: string
    hotelName: string
    address: string
    openMapsLabel: string
}

// Pure presentation: Leaflet single-marker map (RN ViewOnMapTab parity) + address + a
// link out to Google Maps.
const MapSection = ({ lat, lng, mapsUrl, hotelName, address, openMapsLabel }: MapSectionProps) => (
    <Box
        sx={{
            bgcolor: 'neutral.white',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1.5,
            overflow: 'hidden'
        }}
    >
        <Box sx={{ position: 'relative', height: 300 }}>
            <DetailsMapCanvas lat={lat} lng={lng} hotelName={hotelName} />
            <Link
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                sx={{
                    position: 'absolute',
                    bottom: 12,
                    insetInlineEnd: 12,
                    zIndex: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: 'rgba(0,0,0,0.6)',
                    color: 'neutral.white',
                    borderRadius: 999,
                    px: 1.5,
                    py: 0.75
                }}
            >
                <OpenInNewIcon sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'neutral.white' }}>{openMapsLabel}</Typography>
            </Link>
        </Box>
        <Box sx={{ p: 2 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'textColors.primary' }}>{hotelName}</Typography>
            <Typography sx={{ fontSize: 12, color: 'textColors.secondary', mt: 0.25 }}>{address}</Typography>
        </Box>
    </Box>
)

export { MapSection }
export default MapSection
